// src/shell/AppSwitcher.tsx
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { App } from '../lib/types'
import { useAppStore } from './appStore'

function AppDot({ color }: { color: string | null }) {
  return (
    <span style={{
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: color ?? 'var(--muted)',
      flexShrink: 0,
    }} />
  )
}

export function AppSwitcher() {
  const { data, isLoading } = useQuery<{ apps: App[] }, Error>({
    queryKey: ['apps'],
    queryFn: () => api.get<{ apps: App[] }>('/api/apps'),
    staleTime: 1000 * 60 * 5,
  })
  const { activeAppId, setActiveApp } = useAppStore()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const apps = data?.apps ?? []
  const activeApp = apps.find((a) => a.id === activeAppId) ?? apps[0] ?? null

  useEffect(() => {
    if (!activeAppId && apps.length > 0) {
      setActiveApp(apps[0]!.id)
    }
  }, [apps, activeAppId, setActiveApp])

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (isLoading) {
    return (
      <div style={{
        height: '40px',
        background: 'var(--elevated)',
        borderRadius: '8px',
        margin: '0.75rem',
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
    )
  }

  return (
    <div ref={ref} data-testid="app-switcher" style={{ position: 'relative', margin: '0.75rem' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          background: 'var(--elevated)',
          border: '1px solid var(--line)',
          borderRadius: '8px',
          padding: '0.5rem 0.625rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          cursor: 'pointer',
          color: 'var(--fg)',
          fontFamily: 'inherit',
          fontSize: '0.875rem',
          fontWeight: 500,
          textAlign: 'left',
        }}
      >
        {activeApp && <AppDot color={activeApp.color} />}
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activeApp?.name ?? 'Sélectionner une app'}
        </span>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 5l4 4 4-4" stroke="var(--muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: 'var(--elevated)',
          border: '1px solid var(--line-strong)',
          borderRadius: '8px',
          overflow: 'hidden',
          zIndex: 50,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {apps.map((app) => (
            <button
              key={app.id}
              onClick={() => { setActiveApp(app.id); setOpen(false) }}
              style={{
                width: '100%',
                background: app.id === activeAppId ? 'var(--raised)' : 'transparent',
                border: 'none',
                padding: '0.5625rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                color: 'var(--fg)',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                textAlign: 'left',
              }}
            >
              <AppDot color={app.color} />
              <span>{app.name}</span>
              {app.id === activeAppId && (
                <svg style={{ marginLeft: 'auto' }} width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l4 4 6-7" stroke="var(--arancio)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
